import { useLocation } from 'react-router';
import Config from '../config/config';
import { PurchasesDefault } from '../config/purchaseDefault';
import { SingleProductDefault } from '../config/singleProductDefault';
import { IDeviceType, ISegment } from '../config/types';
import { BannerPositions, BannerTypes } from '../constants/Banners';
import { BenefitTypes } from '../constants/BenefitTypes';
import { BundleProductLines } from '../constants/BundleProductLines';
import { CAMPAIGN_TYPE, CampaignConstants } from '../constants/CampaignConstants';
import { Consumers } from '../constants/Consumers';
import CustomerTypeByOption from '../constants/CustomerTypeByOption';
import Languages, { LanguageType } from '../constants/Languages';
import { OrderTypes } from '../constants/OrderTypes';
import PageUrls from '../constants/PageUrls';
import { ProductTypes } from '../constants/ProductTypes';
import ProductTypesUrl from '../constants/ProductTypesUrl';
import { Purchases } from '../constants/Purchases';
import Segments from '../constants/Segments';
import { MAIN_PRODUCT_STATE_SNAPSHOT } from '../constants/SessionStorageKeys';
import { DataSizeValue, MB } from '../constants/Units';
import { IBundleProduct, IBundleProductPrice } from '../entities/manhattan/bundle/types';
import UrlService from '../services/UrlService';
import noImage from '../views/assets/images/no-image.png';

export const detectIE = (): number | boolean => {
  const userAgent = window.navigator.userAgent;

  const msie = userAgent.indexOf('MSIE ');
  if (msie > 0) {
    return parseInt(userAgent.substring(msie + 5, userAgent.indexOf('.', msie)), 10);
  }

  const trident = userAgent.indexOf('Trident/');
  if (trident > 0) {
    const rv = userAgent.indexOf('rv:');
    return parseInt(userAgent.substring(rv + 3, userAgent.indexOf('.', rv)), 10);
  }

  const edge = userAgent.indexOf('Edge/');
  if (edge > 0) {
    return parseInt(userAgent.substring(edge + 5, userAgent.indexOf('.', edge)), 10);
  }

  return false;
};

export const getSectionFromUrl = (): string => {
  const isBusiness = window.location.pathname.match(/(Biznis-korisnici|biznis)|Business|business/g) !== null;

  if (isBusiness) {
    return Consumers.BUSINESS_CONSUMER;
  }

  return Consumers.PRIVATE_CONSUMER;
};

export const isCustomerTypeOptionExisting = (): boolean => {
  const param = new URLSearchParams(window.location.search);
  const options = param.get('options');
  if (options === CustomerTypeByOption.EXISTING_CUSTOMER) {
    return true;
  }
  return false;
};

export const getDeviceType = () => {
  if (window.location.pathname.match(/(Modemi|Modems)/g) !== null) {
    return ProductTypes.MODEM;
  }

  if (window.location.pathname.match(/(Satovi-i-narukvice|Watches-and-bracelets)/g) !== null) {
    return ProductTypes.WATCHES_BRACELETS;
  }

  if (window.location.pathname.match(/(Dodatna-oprema|Additional-accessories)/g) !== null) {
    return ProductTypes.ACCESSORY;
  }

  if (window.location.pathname.match(/(Tableti|Tablets)/g) !== null) {
    return ProductTypes.TABLET;
  }

  if (window.location.pathname.match(/(Laptopovi|Laptops)/g) !== null) {
    return ProductTypes.LAPTOP;
  }

  if (window.location.pathname.match(/(TV)/g) !== null) {
    return ProductTypes.TV;
  }

  if (window.location.pathname.match(/(Fiksni-i-oprema|Optics-equipment)/g) !== null) {
    return ProductTypes.OPTICS_EQUIPMENT;
  }

  return ProductTypes.PHONE;
};

export const getBundleProductLineFromUrl = (): string | null => {
  if (window.location.pathname.match(/(fiksna)/gi) !== null) {
    return BundleProductLines.FTTH;
  }

  if (window.location.pathname.match(/(postpaid-ott)/gi) !== null) {
    return BundleProductLines.POSTPAID_OTT;
  }

  if (window.location.pathname.match(/(ott)/gi) !== null) {
    return BundleProductLines.PURE_OTT;
  }

  return null;
};

export const getDeviceTypeIfExistsInUrl = () => {
  const deviceType = getDeviceType();
  if (deviceType === ProductTypes.PHONE) {
    if (window.location.pathname.match(/(Mobilni-telefoni|Mobile-phones)/g) !== null) {
      return ProductTypes.PHONE;
    }
    return null;
  }
  return deviceType;
};

export const getDeviceTypeByProductLine = (productLine: string) => {
  if (productLine === ProductTypes.PHONE) {
    return ProductTypes.PHONE;
  }

  if (productLine === ProductTypes.TABLET) {
    return ProductTypes.TABLET;
  }

  if (productLine === ProductTypes.MODEM || productLine === ProductTypes.YETTEL_HOME) {
    return ProductTypes.MODEM;
  }

  if (productLine === ProductTypes.LAPTOP) {
    return ProductTypes.LAPTOP;
  }

  if (productLine === ProductTypes.TV) {
    return ProductTypes.TV;
  }

  if (productLine === ProductTypes.CORDLESS_PHONE || productLine === ProductTypes.STB) {
    return ProductTypes.OPTICS_EQUIPMENT;
  }

  if (
    productLine === ProductTypes.MYKI ||
    productLine === ProductTypes.WATCH ||
    productLine === ProductTypes.SMART_BAND
  ) {
    return ProductTypes.WATCHES_BRACELETS;
  }

  // everything else is accessory T1-1529 - used for wishlist
  return ProductTypes.ACCESSORY;
};

export const getActiveLanguage = (): LanguageType => {
  const isEn = window.location.pathname.match(/\/en\//g) !== null;

  if (isEn) {
    return Languages.EN;
  }

  return Languages.SR;
};

export const renderAddress = (address: string): string => {
  return address.split('<br>').join('');
};

export const getLabelsByLanguage = (): any => {
  return require(`../translations/${getActiveLanguage()}`);
};

export const getSectionForSubmit = (section: string, language: string): string => {
  const lngSectionMatrix: any = {
    sr: {
      business: 'Biznis-korisnici',
      consumer: 'Privatni-korisnici',
    },
    en: {
      business: 'Business',
      consumer: 'Consumer',
    },
  };

  return lngSectionMatrix[language][section];
};

export const isCheckout = (): boolean => {
  const isCheckout =
    window.location.pathname.match(
      /(Checkout|checkout)|Kupovina|kupovina|Korpa|Shopping-cart|Ugovor|ugovor|contract|Contract|Additional-products|additional-products|Dodaci|dodaci/g
    ) !== null;

  if (isCheckout) {
    return true;
  }

  return false;
};

export const isMainHeaderOnCheckout = (): boolean => {
  const isMainHeaderOnCheckout =
    window.location.pathname.match(
      /Fiksna|fiksna|Landline|landline|Contract|contract|Ugovor|ugovor|Insurance|insurance|Osiguranje|osiguranje|Postpaid-OTT|postpaid-OTT|OTT|ott|Pregled-kupovine|pregled-kupovine/g
    ) !== null;

  if (isMainHeaderOnCheckout) {
    return true;
  }

  return false;
};

export const isAdditionalProducts = (): boolean => {
  const isAdditionalProducts =
    window.location.pathname.match(/(Additional-products|additional-products)|Dodaci|dodaci/g) !== null;

  return isAdditionalProducts;
};

export const isInsurance = (): boolean => {
  const isInsurance = window.location.pathname.match(/(Insurance|insurance)|Osiguranje|osiguranje/g) !== null;

  if (isInsurance) {
    return true;
  }

  return false;
};

export const isCart = (): boolean => {
  const isCart = window.location.pathname.match(/Korpa|Shopping-cart/g) !== null;

  if (isCart) {
    return true;
  }

  return false;
};

export const isCampaign = (): boolean => {
  const isCampaign = window.location.pathname.match(/\/campaigns\//g) !== null;

  if (isCampaign) {
    return true;
  }

  return false;
};

export const isPrepaidRegistraionPage = (): boolean => {
  const pageUrl = `/${PageUrls.sr.PREPAID_REGISTRATION}`;
  const regex = new RegExp(pageUrl, 'i');
  return window.location.pathname.match(regex) !== null;
};

export const isAdditionalProductsPage = (): boolean => {
  const pageUrlSR = `/${PageUrls.sr.ADDITIONAL_PRODUCTS}`;
  const regexSR = new RegExp(pageUrlSR, 'i');
  const pageUrlEN = `/${PageUrls.en.ADDITIONAL_PRODUCTS}`;
  const regexEN = new RegExp(pageUrlEN, 'i');

  return window.location.pathname.match(regexSR) !== null ||
      window.location.pathname.match(regexEN) !== null;
};

export const getPreviousStateOnAdditionalProductsPage = () => {
  if (isAdditionalProductsPage()) {
    try {
      const savedStringifiedState = sessionStorage.getItem(MAIN_PRODUCT_STATE_SNAPSHOT);

      if (savedStringifiedState) {
        const previousState = JSON.parse(savedStringifiedState);
        return previousState;
      }

      return;
    } catch (_) {
      return;
    }
  }

  return;
};

export const getDefaultPurchase = (): string => {
  const section = getSectionFromUrl();
  const deviceType = getDeviceType();

  if (PurchasesDefault[section as keyof ISegment][deviceType as keyof IDeviceType].cosmos) {
    return Purchases.COSMOS;
  }
  return Purchases.SUBSIDY;
};

export const getCosmosInstallmentFromParam = (): number | null => {
  const isBusiness = getSectionFromUrl() === Consumers.BUSINESS_CONSUMER;
  if (isBusiness) {
    return 24;
  }

  const cosmosInstallmentsFromParam = Number(new URLSearchParams(window.location.search).get('cosmosInstallments'));
  return cosmosInstallmentsFromParam || 36;
};

type price = number | string | null;
export const dividePrice = (value: price): number => {
  if (Number(value) > 0 && value !== null) {
    return Number(value) / 100;
  }
  return 0;
};

export const formatAndDividePrice = (value?: price): string | 0 => {
  if (Number(value) > 0 && value !== null) {
    return formatPrice(Number(value) / 100);
  }

  return 0;
};

export const formatDate = (value: string) => {
  const date = new Date(value);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
};

export const firstLetterToUpperCase = (value: string) => {
  const name = value.toLowerCase();
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const formatPrice = (value: price) => {
  if (Number(value) > 0 && value !== null) {
    const priceString = Math.floor(Number(value)).toString();
    const dotIndex = priceString.indexOf('.');
    return priceString.replace(/\d(?=(?:\d{3})+(?:\.|$))/g, ($0, i) => {
      return dotIndex < 0 || i < dotIndex ? `${$0}.` : $0;
    });
  }

  return 0;
};

export const formatPriceWithDecimal = (value: number | string | null) => {
  if (Number(value) > 0 && value !== null) {
    return `${value},00`;
  }

  return 0;
};

export const removeDotFromPrice = (value: number | string): string | 0 => {
  if (Number(value) > 0) {
    const priceString = value.toString();
    return priceString.replace(/\./g, '');
  }
  return 0;
};

export const replaceSpaceWithUnderscoreChar = (name: string) => {
  return name.replace(/\s+/g, '_');
};

export const convertMBToGB = (mb: number | string | boolean): string => {
  if (typeof mb === 'undefined' || typeof mb === 'boolean' || mb === '' || mb === '-' || mb === 0) {
    return '-';
  }

  if (Number(mb) >= 1000) {
    return `${(Number(mb) / 1000).toFixed()}GB`;
  }

  return `${mb}MB`;
};

export const getSegment = (language: string) => {
  if (getSectionFromUrl() === Consumers.BUSINESS_CONSUMER) {
    return Segments[language].BUSINESS_SEGMENT;
  }
  return Segments[language].PRIVATE_SEGMENT;
};

export const getRoutesByLanguage = (language: string) => {
  let segment = Segments[language].PRIVATE_SEGMENT;

  if (getSectionFromUrl() === Consumers.BUSINESS_CONSUMER) {
    segment = Segments[language].BUSINESS_SEGMENT;
  }

  return `${Config.prefixUrl}/${language}/${segment}/${ProductTypesUrl[language][getDeviceType()]}`;
};

export const mapSubsidyContractByUrlParam = (contractUrlParam: string): number => {
  const subsidyContractMap = new Map([
    ['cena_ugovor06', 6],
    ['cena_ugovor12', 12],
    ['cena_ugovor24', 24],
    ['cena_ugovor36', 36],
    ['cena_ugovor48', 48],
    ['cena_ugovor60', 60],
    ['cena', 0],
  ]);

  if (subsidyContractMap.has(contractUrlParam)) {
    return subsidyContractMap.get(contractUrlParam) as number;
  }

  return 24;
};

export const getCosmosInstallmentsFromUrl = () => {
  if (!window.location.search) return;
  const params = new URLSearchParams(window.location.search);
  const cosmosInstallmentsParam = params.get("cosmosInstallments");
  return Number(cosmosInstallmentsParam);
};

export const mapSubsidyUrlByContract = (contractParam: number): string => {
  const subsidyContractMap = new Map([
    [6, 'cena_ugovor06'],
    [12, 'cena_ugovor12'],
    [24, 'cena_ugovor24'],
    [36, 'cena_ugovor36'],
    [48, 'cena_ugovor48'],
    [60, 'cena_ugovor60'],
    [0, 'cena'],
  ]);

  if (subsidyContractMap.has(contractParam)) {
    return subsidyContractMap.get(contractParam) as string;
  }

  return 'cena_ugovor24';
};

export const getImageSrc = (pathFolder: string, imageName: string, extension: string): string => {
  let imagePath = '';
  try {
    imagePath = require(`../views/assets/images/${pathFolder}${imageName}.${extension}`);
  } catch (err) {
    imagePath = noImage;
  }
  return imagePath;
};

export const prepareUrlForRedirectToCheckout = (language: string, section: string, newPartUrl: string): string => {
  const sectionUrl = getSectionForSubmit(section, language);
  const urlPath = UrlService.getUrlPath();
  return `${urlPath.substring(0, urlPath.indexOf(sectionUrl) + sectionUrl.length)}/${newPartUrl}`;
};

export const prepareUrlForRedirect = (lastUrlReplacePart: string): string => {
  return UrlService.getUrlPath().replace(/\/[^/]*$/, `/${lastUrlReplacePart}`);
};

export const getCampaignPage = (orderType: string, language: string): string => {
  switch (orderType) {
  case OrderTypes.BUNDLE_FTTH:
    return language === Languages.SR ? Config.ftthTariffPackagesSr : Config.ftthTariffPackagesEn;
  case OrderTypes.BUNDLE_OTT:
    return language === Languages.SR ? Config.ottTariffPackagesSr : Config.ottTariffPackagesEn;
  case OrderTypes.BUNDLE_POST_OTT:
    return language === Languages.SR ? Config.postpaidOttTariffPackagesSr : Config.postpaidOttTariffPackagesEn;
  default:
    return '';
  }
};

export const formatFixedNumber = (value: string): string => {
  return `${value.slice(0, 3)} ${value.slice(3, 6)} ${value.slice(6, value.length)}`;
};

export const useQuery = (): URLSearchParams => {
  return new URLSearchParams(useLocation().search);
};

export const removeActiveClass = (activeClassName: string): void => {
  const elems = document.querySelectorAll(`.${activeClassName}`);
  elems?.forEach((el: any) => {
    const re = new RegExp(`\\b${activeClassName}\\b`);
    el.className = el.className.replace(re, '');
    el.innerText = '';
  });
};

type PurchaseForSingleProduct = {
  type: string;
  installments: number;
  priceInRate: string | 0;
  price: string | 0;
};

export const camelToSnake = (camelCaseString: string): string => {
  return camelCaseString.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

export const getPurchaseForSingleProduct = (
  purchaseType: string,
  firstBillPrices: any,
  cosmosPrices: any
): PurchaseForSingleProduct | {} => {
  const defaultInstallmentsCosmos = SingleProductDefault.cosmos.installments;

  if (purchaseType === Purchases.FIRST_BILL) {
    return {
      type: SingleProductDefault.firstBill.type,
      installments: SingleProductDefault.firstBill.installments,
      priceInRate: 0,
      price: firstBillPrices.price,
    };
  }

  if (purchaseType === Purchases.COSMOS) {
    return {
      type: SingleProductDefault.cosmos.type,
      installments: defaultInstallmentsCosmos,
      priceInRate: formatPrice(Number(cosmosPrices.price) / defaultInstallmentsCosmos),
      price: cosmosPrices.price,
    };
  }

  return {};
};

export const getPricesForBundleProduct = (
  useOriginalPrices: boolean,
  bundleProduct: IBundleProduct,
  type: string
): IBundleProductPrice | undefined => {
  return useOriginalPrices
    ? bundleProduct.product.productPrices.find((productPrice: any) => productPrice.type === type)
    : bundleProduct.bundleProductPrices.find((productPrice: any) => productPrice.type === type);
};

export const getCampaignChannelByChnlId = (chnlId: number): string => {
  switch (chnlId) {
  case 0:
    return CampaignConstants.channels.WEBOS;
  case 1:
    return CampaignConstants.channels.CHANNEL01;
  }
  return 'unknown channel';
};

export const getCampaignChnlIdByChannel = (channel: string): number => {
  switch (channel) {
  case CampaignConstants.channels.WEBOS:
    return 0;
  case CampaignConstants.channels.CHANNEL01:
    return 1;
  }
  return -1;
};

export const getInsuranceTypesByPolicyId = (policyId: number, labels: any): string | undefined => {
  return policyId < 6 ? labels.insuranceBasicPackageHeadline : labels.insuranceStandardPackageHeadline;
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
};

export const determineLabelForMonths = (num: number) => {
  const labels = getLabelsByLanguage();

  if (num === 1) {
    return labels.month;
  }

  // 2, 3, 4 meseca.
  if (num <= 4) {
    return labels.month24;
  }

  // od 5 do 20 su [meseci].
  if (num <= 20) {
    return labels.month12;
  }

  // 21, 31, 41 su uvek [mesec].
  if (num % 10 === 1) {
    return labels.month;
  }

  // 22, 23, i 24 su uvek [meseca].
  if (num % 10 === 2 || num % 10 === 3 || num % 10 === 4) {
    return labels.month24;
  }

  // ostali brojevi veći
  return labels.month12;
};

export const validateNumber = (value: string): boolean => {
  if (value !== '' && value.toString().length >= 9 && value.toString().length <= 10 && !isNaN(Number(value))) {
    return true;
  }
  return false;
};

type ProduceValidMBFunction = (value: string, unit: DataSizeValue) => number;

export const produceValidMBValue: ProduceValidMBFunction = (value, unit) => {
  const valueInteger = Number(value);

  if (unit === MB) {
    return valueInteger === 512 ? 500 : valueInteger;
  }

  return valueInteger * 1000;
};

export function replaceAStringWithJSX(array: [], fn: Function) {
  let result: any[] = [];
  for (const element of array) {
    const mapping = fn(element);
    result = result.concat(mapping);
  }
  result.pop();
  return result;
}

export const isUserAgentMobile = () => {
  const mobileUserAgents = [/Android/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];

  return mobileUserAgents.some((userAgent) => {
    return userAgent.exec(navigator.userAgent);
  });
};

export const formatUTCTimeToStandard = (time: string | Date) => {
  const date = new Date(time);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const getBannerType = (position: string): string => {
  if (position === BannerPositions.TOP) return BannerTypes.TOP;
  if (position === BannerPositions.AFTER_FIRST_ROW) return BannerTypes.AFTER_FIRST_ROW;
  return BannerTypes.REGULAR;
};

export const getDataBenefitBoxText = (benefit: any, tariffPackage: any): string => {
  const labels = getLabelsByLanguage();

  switch (benefit?.benefits[0]?.type) {
  case BenefitTypes.DATA:
    if (benefit?.benefits[0]?.value === '2') {
      return `${labels.double} ${labels.moreDataDouble} ${tariffPackage.name}`;
    }
    return `${benefit?.benefits[0]?.value} ${labels.times} ${labels.moreInternetInPackage} ${tariffPackage.name}`;
  case BenefitTypes.FULLSPEEDDATA:
    return `${labels.additionalCapitalLetter} ${benefit?.benefits[0]?.value} GB ${labels.atFullSpeed}`;
  case BenefitTypes.DISCOUNT:
    return '';
  default:
    return '';
  }
};

export const checkIsJmbgValid = (jmbg: number | string) => {
  return String(jmbg).length === 13 && !isNaN(Number(jmbg));
};

export const isDev = (): boolean => process.env.NODE_ENV === 'development';

export const isSoftRenewal = (campaignType: string | null)=>  {
  return campaignType !== CAMPAIGN_TYPE.P2P && campaignType !== CAMPAIGN_TYPE.P2PDJ && campaignType !== CAMPAIGN_TYPE.TP_UPSELL;
};
